import { useEffect, useState } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { fetchMarketData, fetchCloses } from "../utils/marketdata";
import { useFxRates } from "../utils/fxRates";
import { fmtPct, shownDir } from "../utils/formatters";
import { dateNumeric } from "../utils/formatDate";
import MarketWeekCard from "./MarketWeekCard";
import Icon from "./Icon";
import LTR from "./LTR";
import { Amount } from "./Money";
import { Skeleton, Loading } from "./Skeleton";
import IndexChartPanel from "./IndexChartPanel";
import { cellOf } from "./MarketsCard";
import { requestScroll } from "../utils/scrollTarget";

// ─── "תמונת השוק" (sprint 21) ───────────────────────────────────────────────
// At the foot of the market intelligence page, closed by default: the page
// is about the holdings, and this is the backdrop. Closed, one line:
// "S&P 500 X% · VIX Y · $/₪ Z". Open:
//   · one line to the "שווקים" card on the portfolio page (26/10/2026), where
//     the indices, the dollar and the Bank of Israel rate are listed · this
//     page used to repeat the S&P 500, NASDAQ, Dow and USD/ILS
//   · VIX · the value, and its 52-week range of daily closes (Yahoo Finance);
//     a tap opens its chart (IndexChartPanel)
//   · EUR/ILS · the app's one rate store (utils/fxRates.js)
//   · "מתרחש בשוק השבוע" · reports, IPOs and macro releases of the week
// A figure with no source answer is not drawn: its row, or its part of the
// line, is left out.

const fmtNum = (v, d = 2) => Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const toneOf = v => (shownDir(v) === 0 ? UI.text.muted : shownDir(v) > 0 ? UI.gain : UI.loss);

// The change over the last `n` sessions: the last close against the close
// `n` sessions before it. → { pct, from } or null.
export function sessionsChange(closes, n = 5) {
  const list = (closes || []).filter(([, v]) => v > 0);
  if (list.length <= n) return null;
  const last = list[list.length - 1], base = list[list.length - 1 - n];
  return { pct: (last[1] / base[1] - 1) * 100, from: base[0] };
}
// The lowest and highest daily close in the series (a year of them).
export function closeRange(closes) {
  const v = (closes || []).map(([, x]) => x).filter(x => x > 0);
  return v.length ? { low: Math.min(...v), high: Math.max(...v), from: closes[0][0] } : null;
}

const Pct = ({ v, size = 13 }) => (
  <LTR className="si-num" style={{ fontSize: size, fontWeight: 700, color: toneOf(v), fontFamily: FONT.latin }}>{shownDir(v) === 0 ? "0.00%" : fmtPct(v)}</LTR>
);

function Row({ label, value, children, note, onClick }) {
  return (
    <div onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, padding: "10px 0", borderTop: `1px solid ${UI.line.hairline}`, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <LTR style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary, fontFamily: FONT.latin, alignSelf: "flex-start" }}>{label}</LTR>
        {note && <span style={{ fontSize: 11.5, color: UI.text.muted, lineHeight: 1.5 }}>{note}</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: UI.text.primary }}>{value}</span>
        {children}
      </div>
    </div>
  );
}

export default function MarketPicture({ portfolio = [], watchlist = [], onNavigate }) {
  const [open, setOpen] = useState(false);
  const [vixChart, setVixChart] = useState(false);
  const [md, setMd] = useState(null);          // null = loading
  const [hist, setHist] = useState(null);      // { week, vix } once open
  const fx = useFxRates();

  useEffect(() => {
    let alive = true;
    fetchMarketData().then(d => { if (alive) setMd(d || {}); }).catch(() => { if (alive) setMd({}); });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!open || hist) return undefined;
    let alive = true;
    fetchCloses(["^VIX"], { range: "1y" })
      .then(year => { if (alive) setHist({ vix: year?.["^VIX"] || null }); });
    return () => { alive = false; };
  }, [open, hist]);

  const sp = md?.sp500, vix = md?.vix, ils = fx.ILS;
  const parts = [
    sp?.value != null && <span key="sp"><LTR style={{ fontFamily: FONT.latin }}>S&amp;P 500</LTR> <Pct v={sp.change_pct} size={13.5}/></span>,
    vix?.value != null && <span key="vix"><LTR style={{ fontFamily: FONT.latin }}>VIX</LTR> <LTR className="si-num" style={{ fontWeight: 700 }}>{fmtNum(vix.value)}</LTR></span>,
    ils?.rate > 0 && <span key="fx"><LTR style={{ fontFamily: FONT.latin }}>$/₪</LTR> <LTR className="si-num" style={{ fontWeight: 700 }}>{fmtNum(ils.rate, 3)}</LTR></span>,
  ].filter(Boolean);

  return (
    <section data-intel-market style={{ background: UI.surface.card, borderRadius: T.radius.xl, boxShadow: UI.shadow.cardEl, padding: "6px 16px" }}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "transparent", border: "none", padding: "10px 0", minHeight: 52, cursor: "pointer", fontFamily: "inherit", textAlign: "start", color: UI.text.primary }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <span style={{ ...TYPE.heading, margin: 0 }}>תמונת השוק</span>
          {md === null ? <Loading><Skeleton w={180} h={12}/></Loading>
            : parts.length > 0 && (
              <span data-intel-market-line style={{ fontSize: 13, color: UI.text.secondary, display: "flex", flexWrap: "wrap", columnGap: 6, rowGap: 2 }}>
                {parts.map((p, i) => <span key={i} style={{ whiteSpace: "nowrap" }}>{i ? <span style={{ color: UI.text.muted }}>· </span> : null}{p}</span>)}
              </span>
            )}
        </span>
        <Icon name={open ? "chevronUp" : "chevronDown"} size={18} color={UI.text.muted}/>
      </button>

      {open && (
        <div style={{ display: "flex", flexDirection: "column", paddingBottom: 12 }}>
          {/* The indices, the dollar and the Bank of Israel rate are in one
              place, the "שווקים" card (26/10/2026): a link, not a copy. */}
          <button type="button" data-intel-markets-link onClick={() => { requestScroll("markets:us"); onNavigate?.("portfolio"); }}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", background: UI.fill.subtle, border: "none", borderRadius: T.radius.md, padding: "10px 12px", minHeight: 48, cursor: "pointer", fontFamily: "inherit", textAlign: "start", marginBottom: 4 }}>
            <span style={{ fontSize: 13.5, color: UI.text.secondary }}>המדדים בארה״ב, בישראל ובעולם, שער הדולר וריבית בנק ישראל</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: UI.interactive, whiteSpace: "nowrap" }}>בכרטיס "שווקים" ←</span>
          </button>
          {vix?.value != null && (() => {
            const r = hist ? closeRange(hist.vix?.closes) : null;
            return (
              <Row label="VIX" value={<LTR className="si-num">{fmtNum(vix.value)}</LTR>} onClick={() => setVixChart(true)}
                note="התנודתיות הצפויה של S&P 500 ב-30 הימים הקרובים, לפי מחירי האופציות · לחיצה פותחת גרף">
                <span style={{ fontSize: 12, color: UI.text.muted }}>יומי <Pct v={vix.change_pct}/></span>
                {hist === null ? <Loading><Skeleton w={110} h={11}/></Loading>
                  : r && <span style={{ fontSize: 12, color: UI.text.muted }}>טווח <LTR className="si-num">52</LTR> שבועות <LTR className="si-num" style={{ fontWeight: 700, color: UI.text.secondary }}>{fmtNum(r.low)}–{fmtNum(r.high)}</LTR></span>}
              </Row>
            );
          })()}
          {ils?.rate > 0 && fx.EUR?.rate > 0 && (
            <Row label="EUR/ILS" value={<Amount value={ils.rate / fx.EUR.rate} currency="ILS" decimals={3} size={15} weight={800}/>}
              note={fx.EUR.date ? <>נכון ל-<LTR className="si-num">{dateNumeric(fx.EUR.date)}</LTR></> : null}/>
          )}
          <div style={{ borderTop: `1px solid ${UI.line.hairline}`, paddingTop: 12, marginTop: 2 }}>
            <MarketWeekCard portfolio={portfolio} watchlist={watchlist} bare/>
          </div>
          <span style={{ fontSize: 11.5, color: UI.text.muted, lineHeight: 1.6, marginTop: 10 }}>
            <LTR>VIX</LTR>: <LTR>Yahoo Finance</LTR>, ובהיעדרו <LTR>FRED</LTR> · טווח <LTR className="si-num">52</LTR> שבועות: סגירות יומיות מ-<LTR>Yahoo Finance</LTR>
            {ils?.source && <> · שערי חליפין: <LTR>{ils.source}</LTR></>}
          </span>
        </div>
      )}
      {vixChart && vix && (
        <IndexChartPanel series={{ key: "vix", label: "VIX", yahoo: "^VIX", tv: "TVC:VIX", delay: 15, latin: true }}
          cell={cellOf("vix", md, fx)} onClose={() => setVixChart(false)}/>
      )}
    </section>
  );
}
